import { api } from "@/api/client";

/** Проверка отгрузки: скан этикеток паллет, набор заказов, закрытие смены. */

export interface ShipmentPallet {
  id: string;
  /** Что напечатано на этикетке: SSCC (или название паллеты в реестре). */
  number: string;
  scanned_at: string;
  /** Грузоместо реестра; у сканов до перехода отгрузки на реестр — нет. */
  cargo_unit_id: string | null;
  /** Наш SSCC паллеты (18 цифр). */
  sscc: string | null;
  /** Прежний номер документа 1С — у паллеты, импортированной из 1С. */
  archive_number: string | null;
}

/** Паллета, которую принял ЭТОТ скан, и её заказ.
 *
 * ⚠️ Экран берёт её из ответа, а не ищет сравнением строк: одну паллету
 * сканируют SSCC, `(00)…`, element string GS1 и старой этикеткой с номером 1С —
 * отсканированная строка с номером паллеты в смене не совпадает. */
export interface ScannedPallet extends ShipmentPallet {
  order_number: string;
}

export interface ShipmentOrder {
  id: string;
  order_ref: string;
  order_number: string;
  organization_name: string | null;
  kontragent_name: string | null;
  pallets_total: number;
  pallets: ShipmentPallet[];
  expense_number: string | null;
  posted: boolean;
  completed: boolean;
}

export interface ShipmentSession {
  id: string;
  status: string;
  orders: ShipmentOrder[];
  /** Заказ, который нельзя бросить недогруженным (пока он есть, чужие паллеты не принимаются). */
  pending_order_number: string | null;
  pending_left: number;
  /** Только в ответе скана — принятая им паллета. */
  scanned?: ScannedPallet | null;
}

export interface CompleteResult {
  posted: string[];
  /** Заказы, переведённые в «Отгружен».
   *
   * ⚠️ Имя поля осталось с тех пор, когда отгрузка ставила «Завершен».
   * Переименовать его нельзя: вкладку склада не закрывают сутками, а старая
   * читает именно `completed_orders` — после переименования она показывала бы
   * пустой результат успешно завершённой смены. */
  completed_orders: string[];
  warnings: string[];
}

/**
 * Коды ошибок — по ним экран выбирает заголовок плашки.
 *
 * ⚠️ `complete_failed` ставит НЕ бэкенд, а экран, когда падает завершение
 * смены. Без него ошибка завершения показывалась под заголовком «Скан не
 * принят»: кладовщик читал про скан, а не проводилась расходная, и разбор
 * прода 01.09.2026 начался с поиска несуществующей ошибки в скан-цикле.
 */
export type ShipmentErrorCode =
  | "not_found"
  | "duplicate"
  | "order_pending"
  | "not_checked"
  | "closed"
  /** Смену прямо сейчас завершают — скан и снятие ждут конца. */
  | "busy"
  /** Снять нельзя: расходная заказа проведена / паллета отгружена. */
  | "already_posted"
  | "complete_failed"
  | "error";

export interface ShipmentErrorDetail {
  code: ShipmentErrorCode;
  detail: string;
}

export async function getSession(): Promise<ShipmentSession> {
  const { data } = await api.get<ShipmentSession>("/shipment/session");
  return data;
}

/** Скан этикетки паллеты.
 *
 * ⚠️ Таймаут ЯВНЫЙ. У клиента общего таймаута нет намеренно — создание
 * документов в 1С идёт десятками секунд, — но скан это быстрый поиск паллеты
 * по номеру, и зависший запрос тут стоит дороже: пока он «в полёте», экран
 * не принимает следующие сканы, и раздел глохнет до перезагрузки страницы.
 * Лучше честная ошибка через 20 секунд, чем немой экран. Ответ несёт
 * принятую паллету (`scanned`).
 */
export async function scanPallet(code: string): Promise<ShipmentSession> {
  const { data } = await api.post<ShipmentSession>(
    "/shipment/scan", { code }, { timeout: 20_000 },
  );
  return data;
}

export async function removePallet(palletId: string): Promise<ShipmentSession> {
  const { data } = await api.delete<ShipmentSession>(`/shipment/pallets/${palletId}`);
  return data;
}

/** Бросить открытую смену БЕЗ обращения в 1С. Нужна ровно тогда, когда смену
 *  начали с подключением к другой базе: тогда все остальные ручки отвечают 409,
 *  а смена одна на портал — раздел заперт у всех. */
export async function abandonSession(): Promise<ShipmentSession> {
  const { data } = await api.post<ShipmentSession>("/shipment/abandon");
  return data;
}

export async function completeShipment(): Promise<CompleteResult> {
  const { data } = await api.post<CompleteResult>("/shipment/complete");
  return data;
}
